import type { Json, TaskEvent, TaskEventType } from "@estelle/domain";

/** `field_edited` becomes `Field edited`, and anything this switch has not met yet gets the same treatment. */
function humanize(value: string): string {
  const spaced = value.replace(/_/g, " ");
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
}

/**
 * What the activity timeline calls each event type.
 *
 * A `switch` with a `default` rather than a `Record` lookup: `TaskEventType`
 * is a closed union today, but the timeline is append-only and has to
 * render whatever it was actually given, including a member added to the
 * union after this file was last touched — humanising the raw string is
 * legible where a `Record` miss would render nothing at all.
 */
export function taskEventLabel(type: TaskEventType): string {
  switch (type) {
    case "created":
      return "Created";
    case "status_changed":
      return "Status changed";
    case "assigned":
      return "Assigned";
    case "unassigned":
      return "Unassigned";
    case "priority_changed":
      return "Priority changed";
    case "due_date_changed":
      return "Due date changed";
    case "field_edited":
      return "Field edited";
    case "commented":
      return "Commented";
    case "comment_edited":
      return "Comment edited";
    case "linked":
      return "Linked";
    case "unlinked":
      return "Unlinked";
    case "attachment_added":
      return "Attachment added";
    case "attachment_removed":
      return "Attachment removed";
    case "subtask_added":
      return "Subtask added";
    case "watcher_added":
      return "Watcher added";
    case "watcher_removed":
      return "Watcher removed";
    case "deleted":
      return "Deleted";
    case "restored":
      return "Restored";
    default:
      return humanize(type);
  }
}

/**
 * A change-history `before`/`after` value, rendered honestly.
 *
 * Identical in spirit to `eventValue` in `modules/orders/format.ts`: it is
 * `Json`, not a display type, and a plain `String()` on the object or array
 * shape a link or an attachment event carries would print
 * `"[object Object]"` for every such row (`no-base-to-string` catches this
 * for a reason). Not imported from Orders — module directories stay
 * self-contained rather than reaching into a sibling module's internals.
 */
export function eventValue(value: Json | null): string {
  if (value === null) return "—";
  if (typeof value === "string") return value;
  if (typeof value === "number" || typeof value === "boolean") {
    return String(value);
  }
  return JSON.stringify(value);
}

/**
 * Whether an event names a user id in `before` or `after` — `assigned`,
 * `unassigned`, `watcher_added` and `watcher_removed` all do, and the
 * timeline resolves those to a name rather than showing the raw id the way
 * `linked` or `field_edited` legitimately do.
 */
export function namesAUser(type: TaskEventType): boolean {
  return (
    type === "assigned" ||
    type === "unassigned" ||
    type === "watcher_added" ||
    type === "watcher_removed"
  );
}

/** Sort order for `TaskEvent`s: newest first, the way an activity panel reads. */
export function sortEventsNewestFirst(
  events: readonly TaskEvent[],
): TaskEvent[] {
  return [...events].sort((a, b) => (a.occurredAt < b.occurredAt ? 1 : -1));
}
